import {Sequence as ComponentSequence} from 'components/Sequence';
import SequenceAttributeCollection from 'components/SequenceAttributeCollection';
import {CustomField, Strand} from 'constants/MolbioConstants';
import {
  ANNOTATION_MAX_CUSTOM_FIELDS,
  ANNOTATION_MAX_NAME_LENGTH,
  ANNOTATION_MAX_TYPE_LENGTH,
  PolymerFeatureAttributes,
  PolymerFeatureModel,
} from 'constants/SequenceConstants';

import {Sequence} from './Sequence';
import SequenceRegion from './SequenceRegion';

export interface AnnotationAttributes extends PolymerFeatureAttributes {
  visible: boolean;
  name: string;
  annotation_type: string | null;
  color: string;
  notes: string;
  custom_fields: Array<CustomField>;
}

interface ValidateOptions extends Backbone.ModelSetOptions {
  sequence: Sequence | ComponentSequence;
}

interface Annotation {
  get<K extends keyof AnnotationAttributes>(key: K): AnnotationAttributes[K];
  set(obj: Partial<AnnotationAttributes>, options?: ValidateOptions): Annotation;
  set<K extends keyof AnnotationAttributes>(
    key: K,
    value: AnnotationAttributes[K],
    options?: ValidateOptions
  ): Annotation;
  fastSet(obj: Partial<AnnotationAttributes>): Annotation;
  fastSet<K extends keyof AnnotationAttributes>(key: K, value: AnnotationAttributes[K]): Annotation;
  fastUnset<K extends keyof AnnotationAttributes>(key: K): Annotation;
  // Part modification code hackily tacks on these booleans sometimes.
  isDirectStart?: boolean;
  isDirectEnd?: boolean;
}

class Annotation extends SequenceRegion implements PolymerFeatureModel {
  defaults(): Partial<AnnotationAttributes> {
    return {
      ...super.defaults(),
      name: '',
      annotation_type: '',
      strand: 1,
      color: '#7EA7D8',
      notes: '',
      custom_fields: [],
    };
  }

  strand(): Strand {
    return this.get('strand');
  }
  color(): string {
    return this.get('color');
  }
  annotationType(): string | null {
    return this.get('annotation_type');
  }
  name(): string {
    return this.get('name');
  }
  notes(): string {
    return this.get('notes');
  }
  customFields(): Array<CustomField> {
    return this.get('custom_fields');
  }
  /**
   * Return an identity string for the purpose of determining when an annotation is a duplicate
   * of another.
   */
  identityFingerprint(): string {
    return `${this.name()}:${this.start()}:${this.end()}:${this.strand()}`;
  }

  equal(o: Annotation): boolean {
    return (
      this.get('name') === o.get('name') &&
      this.start() === o.start() &&
      this.end() === o.end() &&
      this.strand() === o.strand() &&
      this.color() === o.color()
    );
  }

  validate(
    // We don't disable attempting to save an incomplete annotation; instead, we run that annotation through
    // this function, so start/end can actually be null at this point.
    attrs: Omit<AnnotationAttributes, 'start' | 'end'> & {start: number | null; end: number | null},
    options: ValidateOptions
  ): string | undefined {
    const length = options.sequence.length();
    const startIndex = options.sequence.startIndex();
    if (!attrs.name) {
      return 'An annotation must have a valid name';
    } else if (attrs.name.length > ANNOTATION_MAX_NAME_LENGTH) {
      return `Annotation name must be less than ${ANNOTATION_MAX_NAME_LENGTH} characters`;
    } else if (attrs.annotation_type != null && attrs.annotation_type.length > ANNOTATION_MAX_TYPE_LENGTH) {
      return `Annotation type must be less than ${ANNOTATION_MAX_TYPE_LENGTH} characters`;
    } else if (
      attrs.start == null ||
      attrs.end == null ||
      Number.isNaN(attrs.start) ||
      Number.isNaN(attrs.end)
    ) {
      return 'An annotation must have a valid start and end';
    } else if (length === 0) {
      return 'An annotation cannot be added to 0-length sequence';
    } else if (!(0 <= attrs.start && attrs.start < length && 0 <= attrs.end && attrs.end < length)) {
      return `Start and end values must be between ${startIndex} and ${startIndex - 1 + length}.`;
    } else if (attrs.start >= attrs.end && attrs.end !== 0 && !options.sequence.circular()) {
      return 'Start and end values cannot wrap on a linear sequence';
    } else if (attrs.custom_fields.length > ANNOTATION_MAX_CUSTOM_FIELDS) {
      return 'Annotation has too many custom fields';
    } else {
      return undefined;
    }
  }
}

export class AnnotationCollection extends SequenceAttributeCollection<Annotation> {
  static initClass(): void {
    this.prototype.model = Annotation; // should match component/annotations.js
    this.prototype.attributeName = 'annotations';
  }

  batchName(): string {
    return 'annotations';
  }

  deepClone(offset: number = 0): AnnotationCollection {
    const col = new AnnotationCollection();
    this.each((ann) => col.add(ann.clone(offset)));
    return col;
  }
}

AnnotationCollection.initClass();

export {Annotation, Annotation as Model, AnnotationCollection as Collection};
